/**
 * ProductConfiguration — Module 9 shell (horizontal tabs variant)
 *
 * Routes:
 *   /purchase-flow-revamp/configuration/:cartItemId            (legacy — redirects)
 *   /purchase-flow-revamp/configuration/:cartItemId/:flowId    (Phase A.5)
 *
 * Responsibilities:
 *   1. Compute `tabs: ConfigurationTab[]` from cart × catalogue. Each tab is a
 *      `(cartItemId, flowId)` tuple — a single product can have multiple flows
 *      (e.g. authorities documents that stack `power_of_attorney` +
 *      `property_info`). Redirect to instructions if none; redirect to first
 *      tab if URL params are invalid.
 *   2. Render horizontal chip strip — one per `(cartItem, flow)` tuple. When a
 *      cart item has more than one flow, the chip label is suffixed with the
 *      flow name. Active chip = `(:cartItemId, :flowId)` URL params.
 *   3. **Display-none mounting:** ALL native module panels are mounted
 *      simultaneously. Inactive panels are hidden via CSS `display: none`; the
 *      active panel is `display: block`. Panels mount exactly once per
 *      `(cartItem, flow)` (not on every tab switch), so tab switches are pure
 *      visibility toggles and each module keeps its local UI state alive.
 *   4. The active tab confirms itself once complete (`useAutoConfirmFlow` flips
 *      `cartItemUserConfirmed`). The DTO itself is already persisted in
 *      `productConfigs` by the module's ops-hook `patch`, so there is nothing to
 *      save; the header's "Saved" indicator shows it. The cart sidebar's Proceed
 *      is gated by `selectConfigurationStepValid` and says what is still missing.
 *   5. URL-driven tab switching: clicking a chip calls `navigate`.
 *
 * Native-only note (Wave-3 teardown): all 8 config flows are native modules
 * (`PF_CONFIG_MODULES`). The legacy `LegacyAdapterShell` / `ConfigSessionShim`
 * wrapper, the imperative adapter-ref map, `serializeAdapter`, and the
 * serialize-on-exit effect are all gone — native modules keep their durable
 * state in the PF-v2 store (hydrated by a synchronous `set()` before mount), so
 * there is nothing to serialize on exit and no legacy provider tree to mount.
 * A module reads its DTO from `productConfigs` on render and writes it via its
 * ops-hook `patch`; the F-B1 hydrate-vs-reset race is unreachable by construction.
 *
 * Phase C-B: step nav (Back/Next) lives in `CartSidebar`'s pinned bottom bar
 * (`StepNavSection`). This page owns neither its sticky footer nor commit-on-Next
 * plumbing — those concerns live in `useAutoConfirmFlow` (confirmation) +
 * `StepNavSection` (nav).
 */

import React, { useCallback, useMemo } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { PfChipStrip, type PfChipStripItem } from '../../components/PfChipStrip'
import { ProductDrawer } from '../../components/productDrawer'

import type { ConfigurationFlowId } from 'models/purchaseFlowRevamp'

import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { usePfCatalogue } from 'dataQueries/purchaseFlowRevamp.query'
import { resolveTabsFromCartAndCatalogue, type ConfigTab } from './resolveTabs'
import { needsSourceUploadSurface } from 'utils/purchaseFlowRevamp'
import { ConfigPanelActiveProvider, SourceVisualsSection, useVariantInputImagePolicy } from '../../modules/sourceVisuals'
import { selectFlowComplete, usePfStore } from '../../store'
import { PF_CONFIG_MODULES } from '../../modules/registry'
import { PfPageHeading, PfRetryAlert, StepShell } from '../../components'
import { shellChrome } from '../../components/StepShell.component'
import { PF_STICKY_TOP, gutterBleed } from '../../components/stickyOffsets'
import { usePfEmbed } from '../../routing/usePfEmbed'
import { BEIGE_200 } from 'constants/styling/theme'
import { SelectionStyleProvider } from 'components/common/SelectionStyle'
import { configurationTabLabels } from './chipLabels'
import { useSyncStepWithRoute } from '../../routing/useSyncStepWithRoute'
import { useConfigScreenAnnouncement } from './useConfigScreenAnnouncement'
import { useCartMutationDispatcher } from './useCartMutationDispatcher'
import { useAutoConfirmFlow } from './useAutoConfirmFlow'
import { PfStepPath, usePfPath } from '../../routing/pfBasePath'
import { useRequireActiveSession } from '../../routing/useRequireActiveSession'

// ─── Route params ─────────────────────────────────────────────────────────────

// `type` (not `interface`) so the shape carries an implicit index signature and
// satisfies react-router's `useParams<T extends Record<string, string | undefined>>`
// constraint — interfaces are rejected there (TS interface/Record gotcha).
type ConfigRouteParams = {
  cartItemId: string
  flowId?: string
}

// ─── Tab unit (cartItem × flow) — aliased from resolveTabs.ts ────────────────
//
// `ConfigTab` is the canonical shape. The alias keeps local code readable and
// avoids a breaking rename of every internal reference.
type ConfigurationTab = ConfigTab

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Takes the base resolver rather than reading a constant: the same page serves
 *  the flow's own route and the Propstack embed's (see `pfBasePath`). */
const configPathFor = (
  pfPath: (step?: string) => string,
  cartItemId: string,
  flowId: ConfigurationFlowId
): string => pfPath(`${PfStepPath.CONFIGURATION}/${cartItemId}/${flowId}`)

const tabKey = (cartItemId: string, flowId: ConfigurationFlowId): string =>
  `${cartItemId}|${flowId}`

// ─── Config panel (one per tab, always mounted) ───────────────────────────────

/**
 * Renders a single (cartItemId, flowId) native module panel, confirmed once
 * complete while it is the active tab. All panels are mounted simultaneously;
 * inactive ones are hidden with CSS `display: none` so each module keeps its
 * local UI state across tab switches without a remount + re-render cascade.
 *
 * A flow whose product is produced FROM client visuals but which collects none of
 * its own gets the shared upload above its module, and the module is withheld
 * until a visual arrives — the configuration is about those visuals, so there is
 * nothing to answer before they exist. Composed here rather than inside each
 * module so the rule stays one rule: `needsSourceUploadSurface` reads the
 * catalogue, names no product, and covers the next one for free.
 */
interface AdapterPanelProps {
  tab: ConfigurationTab
  /** When false, the panel is visually hidden via `display: none` but remains
   *  mounted so its module keeps internal state alive across tab switches. */
  isActive: boolean
  onCartMutation: ReturnType<typeof useCartMutationDispatcher>
}

const AdapterPanel: React.FC<AdapterPanelProps> = ({ tab, isActive, onCartMutation }) => {
  const panelKey = tabKey(tab.cartItemId, tab.flowId)
  const { flowId } = tab

  // All 8 flows are native modules — resolve this tab's module from the registry.
  // A missing entry would render nothing (impossible today; every ConfigurationFlowId
  // is registered), so no legacy fallback branch exists.
  const mod = PF_CONFIG_MODULES[flowId]

  const policy = useVariantInputImagePolicy(tab.cartItemId)
  const needsUpload = needsSourceUploadSurface(flowId, policy)
  const hasSourceVisuals = usePfStore((state) => (state.attachments[tab.cartItemId]?.length ?? 0) > 0)
  const configurationUnlocked = !needsUpload || hasSourceVisuals
  useAutoConfirmFlow(tab.cartItemId, flowId, isActive && configurationUnlocked)

  return (
    <Box
      id={`pf-chip-panel-${panelKey}`}
      // The side padding leaves the sections' shadow inside the clip.
      sx={{ display: isActive ? 'block' : 'none', minWidth: 0, overflow: 'hidden', mx: -1.6, px: 1.6, pb: 1.6 }}
      role="tabpanel"
      aria-labelledby={`pf-chip-tab-${panelKey}`}
      aria-hidden={!isActive}
    >

      {/* Every panel is mounted at once, so a hidden line must not claim files the
          client dropped into the tab they are looking at — two lines of one
          product share a FileAPI scope, and both claiming is an update loop. */}
      <ConfigPanelActiveProvider isActive={isActive}>

        {needsUpload &&
          <SourceVisualsSection cartItemId={tab.cartItemId} flowId={flowId} policy={policy} />
        }

        {mod && configurationUnlocked &&
          <mod.Component cartItemId={tab.cartItemId} onCartMutation={onCartMutation} />
        }

      </ConfigPanelActiveProvider>

    </Box>
  )
}

// ─── Shell ────────────────────────────────────────────────────────────────────

/**
 * @page ProductConfiguration
 * Configuration step shell — horizontal tabs variant.
 *
 * @example
 * // Registered in PurchaseFlowRevamp.page.tsx as:
 * <Route path="configuration/:cartItemId" element={<ProductConfiguration />} />
 * <Route path="configuration/:cartItemId/:flowId" element={<ProductConfiguration />} />
 */
export const ProductConfiguration: React.FC = () => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  const { isEmbed } = usePfEmbed()
  const navigate = useNavigate()
  const pfPath = usePfPath()
  const {
    cartItemId: activeCartItemId = '',
    flowId: activeFlowIdParam,
  } = useParams<ConfigRouteParams>()

  // Sync step nav
  useSyncStepWithRoute('configuration')
  // A reload or a deep link arrives with an empty store: nothing to configure, and no session to load
  // the catalogue with.
  useRequireActiveSession(pfPath(PfStepPath.CATALOGUE))

  // Store reads
  const items = usePfStore((s) => s.items)
  const isSessionFailed = usePfStore((s) => s.draft.sessionStatus === 'error')

  // Catalogue (needed to resolve shell.configurationFlows).
  const catalogueQuery = usePfCatalogue()
  const catalogue = catalogueQuery.data

  // Per-(cartItem, flow) user-confirmed flag (Phase C-A) — drives the chip-strip
  // checkmark badge alongside each flow's completeness.
  const cartItemUserConfirmed = usePfStore((s) => s.cartItemUserConfirmed)

  // Shell dispatcher for module-emitted cart mutations (triage-03).
  const onCartMutation = useCartMutationDispatcher(catalogue)

  // Per-chip completeness subscription for the chip strip (MODULE-CONTRACT §4a).
  // Each chip subscribes to `selectFlowComplete(cartItemId, flowId)` for its own
  // badge instead of the parent precomputing it at render time. `selectFlowComplete`
  // reads the native DTO predicate for the flow — all 8 flows are native.
  const chipCompleteSelector = useCallback((id: string) => {
    const [ciId, ...flowParts] = id.split('|')
    return selectFlowComplete(ciId, flowParts.join('|') as ConfigurationFlowId)
  }, [])

  // Phase A — fallback to FE-side (productType|kind) resolver when the BE has
  // not yet emitted shell.configurationFlows. Same gate as CartSidebar and
  // selectConfigurationStepValid — all three must use the same flag value so
  // the tab list and the validity selector always agree.
  const allowFallbackFlowResolver = useFeatureFlag(FeatureFlag.ALLOW_PF_REVAMP_FALLBACK_FLOW_RESOLVER)

  // Shell metadata per item (display name + flow id)
  const shellMetaFor = useCallback((shellProductId: string) => {
    if (!catalogue) return null
    return catalogue.shellProducts.find((s) => s.id === shellProductId) ?? null
  }, [catalogue])

  // Tabs — one per (cartItemId, flowId) tuple.
  // Uses the same `resolveTabsFromCartAndCatalogue` function that
  // `selectConfigurationStepValid` uses so the tab list and the step-valid
  // gate always agree on which (cartItemId, flowId) pairs exist.
  const tabs = useMemo<ConfigurationTab[]>(() => {
    if (!catalogue) return []
    return resolveTabsFromCartAndCatalogue(items, catalogue.shellProducts, allowFallbackFlowResolver)
  }, [items, catalogue, allowFallbackFlowResolver])

  // Which configuration form the client is on — legacy's per-step
  // `enters_order_*_page_screen` resolution, restored for the tabbed layout.
  // Resolved against the real tab list rather than taken from the URL: an
  // unknown `:flowId` is redirected away a moment later, and announcing it
  // first would put a route typo into the funnel as a screen.
  const announcedFlowId = useMemo(
    () => tabs.find(
      (tab) => tab.cartItemId === activeCartItemId && tab.flowId === activeFlowIdParam
    )?.flowId,
    [tabs, activeCartItemId, activeFlowIdParam]
  )
  useConfigScreenAnnouncement(activeCartItemId, announcedFlowId)

  // ── Redirect cases ──────────────────────────────────────────────────────────

  // Without a session the catalogue never loads; the catalogue step is where the session is opened
  // again and offers its retry.
  if (isSessionFailed) return <Navigate to={pfPath(PfStepPath.CATALOGUE)} replace />

  // Wait for catalogue to load before deciding anything
  if (catalogueQuery.isPending) return null

  // Without the catalogue no tab resolves, and treating that as "nothing to configure" skipped the
  // step and its required configuration altogether.
  if (catalogueQuery.isError) {
    return (
      <StepShell>
        <PfRetryAlert
          data-cy="pf-revamp-configuration-catalogue-error"
          message={t('purchase_flow_revamp:catalog.load_error')}
          onRetry={() => { catalogueQuery.refetch() }}
        />
      </StepShell>
    )
  }

  if (tabs.length === 0) {
    return <Navigate to={pfPath(PfStepPath.INSTRUCTIONS)} replace />
  }

  // Resolve the active tab from URL params:
  //   - `:flowId` absent (legacy `/configuration/:cartItemId` shape): pick the
  //     first flow for the cart item.
  //   - `:flowId` present but unknown for this item: pick the first flow.
  //   - cartItemId unknown: pick the first overall tab.
  let activeTabIndex = -1
  if (activeFlowIdParam) {
    activeTabIndex = tabs.findIndex(
      (tab) => tab.cartItemId === activeCartItemId && tab.flowId === activeFlowIdParam
    )
  }
  if (activeTabIndex === -1 && activeCartItemId) {
    // Either flowId missing or unknown — fall back to the first tab for this item.
    const firstForItem = tabs.find((tab) => tab.cartItemId === activeCartItemId)
    if (firstForItem) {
      return <Navigate to={configPathFor(pfPath, firstForItem.cartItemId, firstForItem.flowId)} replace />
    }
  }
  if (activeTabIndex === -1) {
    // Unknown cart item — redirect to first tab overall.
    const first = tabs[0]
    return <Navigate to={configPathFor(pfPath, first.cartItemId, first.flowId)} replace />
  }

  const resolvedActiveIndex = activeTabIndex

  // ── Chip-strip plumbing (Phase C-A / C3) ──────────────────────────────────
  //
  // Build PfChipStripItem[] from `tabs`. Completeness is NOT precomputed here —
  // each chip subscribes to `selectFlowComplete` via the `completeSelector` prop
  // (avoids a render-time recompute in the parent). `isComplete` below is only a
  // fallback for callers that omit `completeSelector`; the strip ignores it here.
  // The chip name is the shell's, which is the PRODUCT GROUP name — so two
  // lines from one group (the two energy certificates) would read identically.
  // Qualify with the flow whenever a name is not unique across the strip, not
  // only when one item happens to need several flows.
  const chipLabels = configurationTabLabels(
    tabs,
    (cartItemId) => shellMetaFor(items.find((i) => i.id === cartItemId)?.shellProductId ?? '')?.displayName ?? cartItemId,
    (flowId) => t(`purchase_flow_revamp:configuration.flow_label.${flowId}`)
  )

  const chipItems: PfChipStripItem[] = tabs.map((tab, index) => ({
    id: tabKey(tab.cartItemId, tab.flowId),
    label: chipLabels[index],
    isComplete: false,
    userConfirmed: !!cartItemUserConfirmed[tab.cartItemId]?.[tab.flowId],
  }))

  const activeChipId = chipItems[resolvedActiveIndex]?.id ?? ''

  const handleChipSelect = (id: string) => {
    const tab = tabs.find((tt) => tabKey(tt.cartItemId, tt.flowId) === id)
    if (tab) navigate(configPathFor(pfPath, tab.cartItemId, tab.flowId))
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <StepShell>

      <PfPageHeading
        title={t('purchase_flow_revamp:configuration.page_title')}
        description={t('purchase_flow_revamp:configuration.page_subtitle')}
      />

      {/* Chip strip — one per (cartItem, flow) tuple. A badge lights once the
          tab is complete and was seen (`cartItemUserConfirmed`). */}
      {/* Pinned under the top bar, so the tabs stay in reach while the heading scrolls away. */}
      {tabs.length > 1 &&
        <Box
          data-cy="pf-revamp-config-tabs"
          sx={{ position: 'sticky', top: PF_STICKY_TOP, zIndex: 8, backgroundColor: BEIGE_200, py: 1.2, ...gutterBleed(shellChrome(isEmbed).contentGutter, BEIGE_200) }}
        >
          <PfChipStrip
            items={chipItems}
            activeId={activeChipId}
            onSelect={handleChipSelect}
            completeSelector={chipCompleteSelector}
            markIncomplete
            ariaLabel={t('purchase_flow_revamp:configuration.tab_strip_aria')}
          />
        </Box>
      }

      {/* Native module panels — one per (cartItem, flow) tuple. ALL panels mount
          simultaneously; inactive panels are hidden via `display: none` (F-O20)
          so each module keeps its local UI state across tab switches. No legacy
          provider shell / session shim is needed — native modules read/write the
          PF-v2 store directly. */}
      <SelectionStyleProvider value="ring">
        {tabs.map((tab) => {
          const key = tabKey(tab.cartItemId, tab.flowId)
          const isActive = resolvedActiveIndex >= 0 && tabs[resolvedActiveIndex] === tab
          return (
            <AdapterPanel
              key={key}
              tab={tab}
              isActive={isActive}
              onCartMutation={onCartMutation}
            />
          )
        })}
      </SelectionStyleProvider>

      {/* Product drawer mount — also lives on Catalogue.page.tsx. Needed here so
          openForEdit calls from CartSidebar render
          the drawer overlay instead of writing to the slice with no consumer. */}
      <ProductDrawer />

    </StepShell>
  )
}
